import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { signOut as cognitoSignOut } from "aws-amplify/auth";
import { useAuth } from "../context/AuthContext";
import "../styles/auth.css";

export default function SignIn() {
  const { signIn, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If already authenticated, redirect to dashboard
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signIn(email, password);
      navigate("/dashboard");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign in failed";
      // If it's an "already signed in" type error, force sign out and retry
      if (msg.toLowerCase().includes("already") || msg.toLowerCase().includes("signed in")) {
        try {
          await cognitoSignOut({ global: true });
          await signIn(email, password);
          navigate("/dashboard");
          return;
        } catch (retryErr) {
          setError(retryErr instanceof Error ? retryErr.message : "Sign in failed");
        }
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceSignOut = async () => {
    try {
      await cognitoSignOut({ global: true });
      window.location.reload();
    } catch { /* ignore */ }
  };

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <h1>Welcome back</h1>
        <p className="subtitle">Sign in to your Business System account</p>

        {error && (
          <div className="error-message">
            {error}
            {error.toLowerCase().includes("already") && (
              <button onClick={handleForceSignOut} style={{ display: "block", marginTop: "0.5rem", background: "none", border: "none", color: "#2563eb", fontSize: "0.85rem", cursor: "pointer", padding: 0 }}>
                Force sign out and try again
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
            <div style={{ textAlign: "right", marginTop: "0.4rem" }}>
              <Link to="/forgot-password" style={{ fontSize: "0.825rem", color: "#6b7280" }}>
                Forgot password?
              </Link>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="auth-footer">
          Don't have an account? <Link to="/signup">Create one</Link>
        </div>
      </div>
    </div>
  );
}
